import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { ShowForPreviewPanel } from "./ShowForPreviewPanel";

interface PreviewPanelShowListProps {
  instruction: Partial<server.dto.ListShowInternalInstruction>
}

const PreviewPanelShowList = (props: PreviewPanelShowListProps) => {
  const perPage = 5;
  const [instruction, setInstruction] = useState<server.dto.ListShowInternalInstruction>({
    ...props.instruction,
    order: "dateDesc",
    page: 1,
    perPage,
  });

  const api = useCustomQuery({
    queryFn: () => window.CoreApi.Show.PaginatedInternalEnvironment(instruction),
    queryKey: ["shows", instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.data.pagination);
    }
  });

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, perPage, paginate);
  const items = api.data?.data?.data || [];

  return <div className="flex-column gap">
    <div className="nav-text-regular">{site.currentEnvironment.show.plural}</div>
    {api.isLoading && <Loading />}
    {!api.isLoading && items.length === 0 && <NoDataAvailableDiv />}

    {items.length > 0 && <>
      {items.map(show => <ShowForPreviewPanel key={show.showId} show={show} />)}
      <div className="flex justify-end">
        {pagination.Component}
      </div>
    </>}
  </div>
};

interface PreviewPanelShowListExternalProps {
  instruction: Partial<server.dto.ListShowExternalInstruction>
}

const PreviewPanelShowListExternal = (props: PreviewPanelShowListExternalProps) => {
  const perPage = 5;
  const [instruction, setInstruction] = useState<server.dto.ListShowExternalInstruction>({
    ...props.instruction,
    page: 1,
    perPage,
  });

  const api = useCustomQuery({
    queryFn: () => window.CoreApi.Show.PaginatedExternalEnvironment(instruction),
    queryKey: ["shows", instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.data.pagination);
    }
  });

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, perPage, paginate);
  const items = api.data?.data?.data || [];

  return <div className="flex-column gap">
    <div className="nav-text-regular">{site.currentEnvironment.show.plural}</div>
    {api.isLoading && <Loading />}
    {!api.isLoading && items.length === 0 && <NoDataAvailableDiv />}

    {items.length > 0 && <>
      {items.map(show => <ShowForPreviewPanel key={show.showId} show={{
        ...show,
        title: show.eventName ?? "...",
      }} />)}
      <div className="flex justify-end">
        {pagination.Component}
      </div>
    </>}
  </div>
}

interface VenuePreviewPanelShowListProps {
  venueId: number
}

export const VenuePreviewPanelShowList = ({ venueId }: VenuePreviewPanelShowListProps) =>
  <PreviewPanelShowList instruction={{
    venueIds: [venueId],
  }} />

interface ProductionPreviewPanelShowListProps {
  productionId: number
  isInternal: boolean
}

export const ProductionPreviewPanelShowList = ({ productionId, isInternal }: ProductionPreviewPanelShowListProps) =>
  isInternal ? <PreviewPanelShowList instruction={{
    productionIds: [productionId],
  }} /> : <PreviewPanelShowListExternal instruction={{
    productionIds: [productionId],
  }} />